import React, { FC, ReactElement, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Popover, Tooltip } from 'antd';
import { OrganizationalUser } from '@app/domain/userManagement/organizationUsers';
import { DeletableChip } from '@app/components/Chip';
import Button from '@app/components/Button';

interface Props {
  id: string;
  dataTestId?: string;
  selectedUsers: OrganizationalUser[];
  deselectUser(tenantUserId: string): void;
}

const MAX_SHOWN_CHIPS = 3;

const SelectedUsersChips: FC<Props> = ({ id, dataTestId = id, selectedUsers, deselectUser }) => {
  const [isAllSelectedUsersVisible, setIsAllSelectedUsersVisible] = useState(false);
  const shownUsers = selectedUsers.slice(0, MAX_SHOWN_CHIPS);
  const hiddenUsersCount = selectedUsers.length - shownUsers.length;

  // The popup unmounts once every user fits as a chip, and must not come back open by itself
  useEffect(() => {
    if (!hiddenUsersCount) {
      setIsAllSelectedUsersVisible(false);
    }
  }, [hiddenUsersCount]);

  if (!selectedUsers.length) {
    // Holds a chip's height, so the first selection doesn't push the table down
    return (
      <ChipsLine aria-hidden>
        <PlaceholderChip id={`${id}-placeholder`} onDelete={(): void => undefined}>
          &nbsp;
        </PlaceholderChip>
      </ChipsLine>
    );
  }

  const nameOf = (organizationalUser: OrganizationalUser): string => organizationalUser.name ?? organizationalUser.tenantUserId;
  const renderChip = (organizationalUser: OrganizationalUser, chipId: string, chipDataTestId: string): ReactElement => (
    <DeletableChip
      key={organizationalUser.tenantUserId}
      id={`${chipId}-${organizationalUser.tenantUserId}`}
      dataTestId={`${chipDataTestId}-${organizationalUser.tenantUserId}`}
      appearance='outline'
      colorScheme='primary'
      onDelete={(): void => deselectUser(organizationalUser.tenantUserId)}
    >
      {nameOf(organizationalUser)}
    </DeletableChip>
  );

  return (
    <ChipsLine data-testid={dataTestId}>
      <ShownChips>
        {shownUsers.map((organizationalUser) => (
          <Tooltip key={organizationalUser.tenantUserId} title={nameOf(organizationalUser)}>
            <ShownChip>{renderChip(organizationalUser, `${id}-chip`, `${dataTestId}-chip`)}</ShownChip>
          </Tooltip>
        ))}
      </ShownChips>
      {hiddenUsersCount > 0 && (
        <Popover
          trigger='click'
          visible={isAllSelectedUsersVisible}
          onVisibleChange={setIsAllSelectedUsersVisible}
          placement='bottomRight'
          title={`${selectedUsers.length} selected`}
          content={
            <AllSelectedUsers data-testid={`${dataTestId}-all`}>
              {selectedUsers.map((organizationalUser) =>
                renderChip(organizationalUser, `${id}-all-chip`, `${dataTestId}-all-chip`),
              )}
            </AllSelectedUsers>
          }
        >
          <MoreButton
            id={`${id}-more`}
            dataTestId={`${dataTestId}-more`}
            appearance='text'
            colorScheme='primary'
            onClick={(): void => setIsAllSelectedUsersVisible(true)}
          >
            +{hiddenUsersCount} more
          </MoreButton>
        </Popover>
      )}
    </ChipsLine>
  );
};

export default SelectedUsersChips;

// A grid, so the chips' column can shrink to nothing while "+N more" keeps its width
const ChipsLine = styled.div`
  display: grid;
  grid-template-columns: minmax(0, max-content) auto;
  justify-content: start;
  align-items: center;
  gap: 6px;
  // Room for three readable names, so the selection line wraps before the chips shrink to slivers
  flex: 1 1 460px;
`;

// One line whose chips shrink with an ellipsis rather than wrap, so a selection never pushes the table down
const ShownChips = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
`;

const ShownChip = styled.span`
  display: flex;
  min-width: 0;
  max-width: 200px;

  & > * {
    min-width: 0;
  }
`;

const MoreButton = styled(Button)`
  white-space: nowrap;
`;

const PlaceholderChip = styled(DeletableChip)`
  visibility: hidden;
`;

const AllSelectedUsers = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  max-width: 360px;
  max-height: 320px;
  overflow-y: auto;

  // A scrolling flex column would otherwise squeeze the chips to fit its height
  & > * {
    flex-shrink: 0;
    max-width: 100%;
  }
`;
